<script lang="ts" setup>
import { GetTeamBotsDocument, UpdateBotTeamsMutationDocument } from './TeamModalBots.generated'

const props = defineProps<{
  teamId: string | undefined
}>()

const botEdits = ref<EditSet>(newEditSet())

const { data: botsData, execute: refetchBots } = useQuery({
  query: GetTeamBotsDocument,
  tags: ['bots'],
  cachePolicy: 'network-only',
})

const allBots = computed(() => botsData.value?.bots ?? [])

const serverBotIds = computed<Set<string>>(() => {
  if (!props.teamId) return new Set()
  return new Set(allBots.value.filter((b) => b.teams?.some((t) => t.id === props.teamId)).map((b) => b.id))
})

const currentBotIds = computed<Set<string>>(() => {
  const result = new Set(serverBotIds.value)
  for (const id of botEdits.value.removed) result.delete(id)
  for (const id of botEdits.value.added) result.add(id)
  return result
})

const currentBots = computed(() => allBots.value.filter((b) => currentBotIds.value.has(b.id)))

const availableBotOptions = computed<SelectMenuOption[]>(() =>
  allBots.value.filter((b) => !currentBotIds.value.has(b.id)).map((b) => ({ label: b.name, value: b.id })),
)

const { execute: updateBotTeams } = useMutation(UpdateBotTeamsMutationDocument, {
  refetchTags: ['bots'],
})

function addBot(botId: string) {
  if (!botId || currentBotIds.value.has(botId)) return
  if (botEdits.value.removed.has(botId)) {
    botEdits.value.removed.delete(botId)
  } else {
    botEdits.value.added.add(botId)
  }
}

function removeBot(botId: string) {
  if (botEdits.value.added.has(botId)) {
    botEdits.value.added.delete(botId)
  } else {
    botEdits.value.removed.add(botId)
  }
}

function reset() {
  botEdits.value = newEditSet()
}

async function applyBotChanges(teamId: string): Promise<boolean> {
  const toAdd = Array.from(botEdits.value.added).filter((id) => !serverBotIds.value.has(id))
  const toRemove = Array.from(botEdits.value.removed).filter((id) => serverBotIds.value.has(id))

  if (toAdd.length === 0 && toRemove.length === 0) return true

  // Refetch to avoid clobbering team memberships modified by other users since this modal was opened.
  await refetchBots()

  for (const botId of toAdd) {
    const bot = allBots.value.find((b) => b.id === botId)
    const currentTeamIds = bot?.teams?.map((t) => t.id) ?? []
    if (currentTeamIds.includes(teamId)) continue
    const response = await updateBotTeams({ input: { id: botId, teamIds: [...currentTeamIds, teamId] } })
    const error = handleMutationError({ errors: response.data?.updateBot?.errors, combinedError: response.error })
    if (error) return false
  }

  for (const botId of toRemove) {
    const bot = allBots.value.find((b) => b.id === botId)
    const currentTeamIds = bot?.teams?.map((t) => t.id) ?? []
    if (!currentTeamIds.includes(teamId)) continue
    const response = await updateBotTeams({
      input: { id: botId, teamIds: currentTeamIds.filter((id) => id !== teamId) },
    })
    const error = handleMutationError({ errors: response.data?.updateBot?.errors, combinedError: response.error })
    if (error) return false
  }
  return true
}

defineExpose({
  applyBotChanges,
  reset,
})
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="mb-4 text-lg font-semibold">Bots</div>

    <div v-if="currentBots.length > 0" class="min-h-0 flex-1 overflow-y-auto">
      <div v-for="bot in currentBots" :key="bot.id" class="flex items-center justify-between py-2 pr-3">
        <div class="flex items-center gap-2.5">
          <UIcon name="light:robot" class="size-5 text-gray-500" />
          <span class="text-sm font-medium text-gray-900 dark:text-white">{{ bot.name }}</span>
        </div>
        <UButton icon="light:xmark" variant="ghost" color="gray" size="xs" @click="removeBot(bot.id)" />
      </div>
    </div>
    <div v-else class="flex-1 text-sm text-gray-500">No bots added yet.</div>

    <SelectMenu
      :model-value="null"
      :options="availableBotOptions"
      placeholder="Add a bot..."
      leading-icon="light:magnifying-glass"
      option-attribute="label"
      value-attribute="value"
      size="md"
      class="mt-4"
      searchable
      @update:model-value="addBot"
    />
  </div>
</template>
